import { useMutation, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '@utils/consts';
import { DocumentResp } from './create';
import { queryFetch } from '@services/api';
import { useAppContext } from '@contexts/app';
import { DocumentsQueryType, getDocumentsUrl } from '.';
import { useJobRefetch } from '@hooks/job/refetch';

export const useRemoveDocument = ({
  queryType,
  jobId,
  subtaskId,
  callback,
}: {
  queryType: DocumentsQueryType;
  jobId?: string;
  subtaskId?: string;
  callback?: (data: DocumentResp) => void;
}) => {
  const { isForeman } = useAppContext();
  const { refetch, stages, subtask } = useJobRefetch();
  const queryClient = useQueryClient();

  const { mutate, isPending } = useMutation({
    mutationFn: (id?: string): Promise<DocumentResp> =>
      queryFetch({ url: `${getDocumentsUrl(queryType)}/${id}`, opt: { method: 'DELETE' } }),
    onMutate: async (id) => {
      if (queryType !== 'subtask') return;
      const documents =
        subtask.get(jobId, subtaskId)?.documents?.filter((el) => el.id !== id) || [];

      return stages.update({
        jobId,
        subtask: {
          id: subtaskId,
          completed: Boolean(documents.length),
          documents,
        },
      });
    },
    onError: (err, _, prev) => {
      console.error(err);
      if (queryType !== 'subtask') return;
      stages.restore(jobId, prev);
    },
    onSuccess: async ({ success }) => {
      if (!success) return;
      if (isForeman) await queryClient.refetchQueries({ queryKey: queryKeys.files });
      else await refetch();
    },
    onSettled: (data) => {
      if (data && callback) callback(data);
    },
  });

  return {
    mutate,
    isLoading: isPending,
  };
};
